import withClearCache from "ClearCache";
import PageNotFound from "screens/Shared/PageNotFound";
import Notifications from "utils/notifications";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { createTheme } from "theme";
import "index.css";
import PrivateRoute from "global/components/PrivateRoute/PrivateRoute";
import { useAppSelector } from "hooks/use-app-select";
import { selectAccount, selectAuthenticated } from "redux/authSlice";
import Layout from "layouts/Layout";
import { matchPath, Route, Router, Switch } from "react-router-dom";
import history from "utils/history";
import Login from "screens/Auth/Login";
import ForgotPassword from "screens/Auth/ForgotPassword";
import NewPassword from "screens/Auth/NewPassword";
import OTP from "screens/Auth/OTP";
import viewpaths from "global/constants/ViewPathConstants";
import ErrorBoundary from "global/components/ErrorBoundary/ErrorBoundary";
import { ScrollToTop } from "utils/ScrollToTop";
import { selectTheme } from "redux/preferencesSlice";
import SetupMFA from "screens/Auth/SetupMFA";
import McpAuthorize from "screens/Auth/Mcp/McpAuthorize";
import AwsMarketplaceSetup from "screens/Auth/AwsMarketplace/AwsMarketplaceSetup";
import OAuthConsent from "screens/Auth/Mcp/OAuthConsent";
import AuthGuard from "global/components/PrivateRoute/AuthGuard";
import Dashboard from "screens/Dashboard/Dashboard";
import MyPlan from "screens/Settings/MyPlan/MyPlan";
import MyProfile from "screens/Settings/MyProfile/MyProfile";
import SettingsIndex from "screens/Settings/SettingsIndex";
import Register from "screens/Auth/Register";
import RegistrationSuccess from "screens/Auth/RegistrationSuccess";
import RoleManagement from "screens/Settings/RoleManagement/RoleManagement";
import Workspaces from "screens/Settings/Workspaces/Workspaces";
import WorkspaceRoles from "screens/Settings/Workspaces/WorkspaceRoles";
import WorkspaceMembers from "screens/Settings/Workspaces/WorkspaceMembers";
import WorkspaceDetails from "screens/Settings/Workspaces/WorkspaceDetails";
import WorkspaceBranding from "screens/Settings/Workspaces/WorkspaceBranding";
import WorkspaceEnrollment from "screens/Settings/Workspaces/WorkspaceEnrollment";
import Tickets from "screens/Tickets/Tickets";
import ViewTicket from "screens/Tickets/ViewTicket/ViewTicket";
import Security from "screens/Settings/Security/Security";
import SecurityPolicies from "screens/Settings/SecurityPolicies/SecurityPolicies";
import EnterpriseSSOPage from "screens/Settings/EnterpriseSSO/EnterpriseSSOPage";
import Clarity from "@microsoft/clarity";
import { initMixpanel, syncSessionReplayWithPath } from "utils/mixpanel";
import { isCredentialBearingUrl } from "utils/analyticsPaths";
import urls from "global/constants/UrlConstants";
import strings from "./global/constants/StringConstants";
import { useCallback, useEffect, useMemo, useState } from "react";
// import { useAppDispatch } from "redux/hooks";
import MySignRequests from "screens/MySignRequests/MySignRequests";
import Contacts from "screens/Contacts/Contacts";
import Configurations from "screens/Configurations/Configurations";
import {
  AccountContext,
  GeneralContext,
  initialSelectedField,
  initialSelectedFile,
  initialSelectedParticipant,
  initialSignRequest,
} from "Context";
import {
  AccountDetails,
  AppPlan,
  Card,
  FileField,
  PlanLimit,
  RequestFile,
  SignRequest,
} from "models/interfaces";
import MyTemplates from "screens/Templates/MyTemplates";
import Team from "screens/Settings/Team/Team";
import {
  fetchAccountPlanDetails,
  fetchPlanUsage,
} from "Services/CommonServices";
import { loadFeatures } from "redux/featuresSlice";
import ViewSignRequest from "screens/MySignRequests/ViewRequest/ViewSignRequest";
import { hasAccessTo } from "layouts/Config";
import VerifyPassword from "screens/Onboarding/components/VerifyPassword";
import CheckYourMail from "screens/Onboarding/components/CheckYourMail";
import SignRequestComponent from "screens/SignRequest/SignRequestComponent";
import BulkSendComponent from "screens/BulkSend/BulkSendComponent";
import BulkBatchStatus from "screens/BulkSend/BulkBatchStatus";
import BulkSigningQueue from "screens/BulkSend/BulkSigningQueue";
import ParticipantScreen from "screens/SignRequest/ParticipantScreen/ParticipantScreen";
import DeclineRequestPage from "screens/SignRequest/ParticipantScreen/components/DeclineRequestPage";
import EmbedSignScreen from "embed/EmbedSignScreen";
import EmbedEditorScreen from "embed/EmbedEditorScreen";
import { EMBED_EDITOR_PATH, EMBED_SIGN_PATH, IS_EMBED } from "embed/embedMode";
import InPersonHostLinkScreen from "screens/InPersonSigning/InPersonHostLinkScreen";
import LinkExpiredComponent from "screens/LinkExpired/LinkExpiredComponent";
import InActiveUserModel from "utils/InActiveUserModel";
// import { setScreen } from "redux/appSlice";
// import { RootState } from "utils/store";
import DirectRegister from "screens/Onboarding/components/DirectRegister";
import { GoogleOAuthProvider } from "@react-oauth/google";
import OAuthRegister from "screens/Auth/OAuthRegister";
import { SkipLink } from "components/skip-link";
import { RouteAnnouncer } from "components/route-announcer";
import { useAppDispatch } from "redux/hooks";
import { setScreen } from "redux/appSlice";
import RenewableDocuments from "screens/RenewableDocuments/RenewableDocuments";
import RenewableDocumentsList from "screens/RenewableDocuments/RenewableDocumentsList";
import EnvelopesReport from "screens/Reports/views/EnvelopesReport";
import ReportsTabsPage from "screens/Reports/views/ReportsTabsPage";
import InsightsPage from "screens/Insights/InsightsPage";
import ContractSearchComponent from "screens/ContractSearch/ContractSearchComponent";
import ContractCompareComponent from "screens/ContractCompare/ContractCompareComponent";
import Branding from "screens/Settings/Branding/Branding";
import DocumentSettings from "screens/Settings/DocumentSettings/DocumentSettings";
import ApiKeys from "screens/Settings/ApiKeys/ApiKeys";
import Delegation from "screens/Settings/Delegation/Delegation";
import ExpiryPassword from "screens/Auth/ExpiryPassword";
import WebFormsList from "screens/WebForms/WebFormsList";
import WebFormBuilder from "screens/WebForms/WebFormBuilder";
import AiWebFormBuilder from "screens/WebForms/AiBuilder/AiWebFormBuilder";
import SelfSignRequestComponent from "screens/SignRequest/components/SelfSignRequestComponent";
import CloudStorageOAuthCallback from "global/components/CloudStoragePicker/CloudStorageOAuthCallback";
import SsoCallback from "screens/Auth/SsoCallback";
import { TourProvider } from "tour";

const App = () => {
  const themePreference = useAppSelector(selectTheme);
  // const dispatch = useAppDispatch();

  const theme = createTheme({
    colorPreset: "inkfree",
    contrast: "normal",
    direction: "ltr",
    paletteMode: themePreference,
    responsiveFontSizes: true,
  });

  // const setAspectRatio = () => {
  //   const isMobile = window.innerWidth / window.innerHeight < 1; //useMediaQuery(theme.breakpoints.down("sm"));
  //   console.log("isMobile", isMobile)
  //   dispatch(setScreen(isMobile));
  // };
  //   const setAspectRatio = () => {
  //   const smallerDimension = Math.min(window.innerWidth, window.innerHeight);
  //   const isMobile = smallerDimension <= 899;
  //   dispatch(setScreen(isMobile));
  // };

  // useEffect(() => {
  //     setAspectRatio();
  //     window.addEventListener("resize", setAspectRatio);
  //     return () => window.removeEventListener("resize", setAspectRatio);
  //   }, []);

  // const setAspectRatio = () => {
  //   const width = window.innerWidth;

  //   const isTouchDevice =
  //     "ontouchstart" in window || navigator.maxTouchPoints > 0;

  //   // Treat all touch devices (mobile + tablet) OR small screens as mobile
  //   const isMobile = isTouchDevice || width <= 1024;
  //   console.log('isMobile', isMobile)

  //   dispatch(setScreen(isMobile));
  // };

  //    const setAspectRatio = () => {
  //   const width = window.innerWidth;
  //   const height = window.innerHeight;
  //   const isLandscape = width > height;

  //   let isMobile: boolean;

  //   if (isLandscape) {
  //     // Landscape mode
  //     // Phone landscape:  height ≤ 480  (e.g. 812x375 → height=375)
  //     // Tablet landscape: height 481-899 (e.g. 1024x768 → height=768)
  //     // Desktop:          height ≥ 900
  //     isMobile =
  //       width < 900 || (width < 1024 && height <= 899 && height > 480)
  //         ? height <= 480 // only phones in landscape
  //         : false;
  //   } else {
  //     // Portrait mode
  //     // Phone portrait:  width ≤ 480  (e.g. 375x812 → width=375)
  //     // Tablet portrait: width 481-899 (e.g. 768x1024 → width=768)
  //     // Desktop:         width ≥ 900
  //     isMobile = width <= 899;
  //   }
  //   dispatch(setScreen(isMobile));
  // };

  useEffect(() => {
    // Analytics is PROD-only, and this is the single gate for all of it.
    if (!urls.PROD) return;

    let clarityStarted = false;

    // Both tools record the URL, and these paths carry a credential in their
    // query string — the IdP code/state on /sso/callback, the guest signer's
    // access token on the signing screens. Neither tool redacts it, so neither
    // starts there. See CREDENTIAL_BEARING_PATHS for why masking is not enough.
    //
    // Driven off every route change, not just mount: starting on such a path
    // must not cost the whole session's analytics. Someone who opens a signing
    // link and later navigates into the app gets picked up on the first clean
    // route instead of staying dark for the rest of the tab's life.
    const startAnalyticsWhenClear = (pathname: string, search: string) => {
      // Embedded Components: no session replay or analytics inside a partner's iframe.
      if (IS_EMBED) return;
      if (!isCredentialBearingUrl(pathname, search)) {
        if (!clarityStarted) {
          clarityStarted = true;
          Clarity.init(strings.CLARITY_PROJECT_ID);
        }
        initMixpanel(); // idempotent, and self-guarded on the same check
      }
      // Runs on every route either way: it is what pauses replay on the way in
      // and resumes it on the way out. Clarity has no equivalent control — its
      // API exposes no stop — so once started it keeps recording.
      syncSessionReplayWithPath(pathname, search);
    };

    startAnalyticsWhenClear(window.location.pathname, window.location.search);
    return history.listen((location) =>
      startAnalyticsWhenClear(location.pathname, location.search),
    );
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        {IS_EMBED ? (
          // Embedded Components: no Google sign-in script inside a partner's iframe (no login there).
          <>
            <SkipLink />
            <ClearCacheComponent mode={themePreference} />
          </>
        ) : (
          <GoogleOAuthProvider clientId={strings.GOOGLE_OAUTH_CLIENT_ID}>
            {/* Skip link must be first focusable element - WCAG 2.4.1 */}
            <SkipLink />
            <ClearCacheComponent mode={themePreference} />
          </GoogleOAuthProvider>
        )}
      </ErrorBoundary>
    </ThemeProvider>
  );
};

const routes = [
  {
    path: `${viewpaths.dashboardViewPath}`,
    component: Dashboard,
  },
  {
    path: `${viewpaths.createSignRequestsViewPath}`,
    component: SignRequestComponent,
    hideNavs: true,
  },
  {
    path: `${viewpaths.createSelfSignRequestPath}`,
    component: SelfSignRequestComponent,
    hideNavs: true,
  },
  {
    // Full-bleed like the create-request wizard: its own header carries the
    // stepper, so the app chrome would only crowd it.
    path: `${viewpaths.bulkSendViewPath}`,
    component: BulkSendComponent,
    hideNavs: true,
  },
  {
    // Before the batch page, or `/bulk/batch/x` would match it first.
    path: `${viewpaths.bulkBatchViewPath}/:id/${viewpaths.bulkQueueSegment}`,
    component: BulkSigningQueue,
  },
  {
    // A normal authenticated page — the batch is a peer of an envelope, so it
    // sits in the app chrome like the request detail view does.
    path: `${viewpaths.bulkBatchViewPath}/:id`,
    component: BulkBatchStatus,
  },
  {
    path: "/requests/add",
    component: CloudStorageOAuthCallback,
    hideNavs: true,
  },
  {
    path: `${viewpaths.updateSignRequestsViewPath}`,
    component: SignRequestComponent,
    hideNavs: true,
    props: {
      updateRequest: true,
    },
  },
  {
    path: `${viewpaths.editDraftViewPath}`,
    component: SignRequestComponent,
    hideNavs: true,
    props: {
      editDraft: true,
    },
  },
  {
    path: `${viewpaths.copySignRequestViewPath}`,
    component: SignRequestComponent,
    hideNavs: true,
    props: {
      copyRequest: true,
    },
  },
  {
    path: `${viewpaths.createTemplateViewPath}`,
    component: SignRequestComponent,
    hideNavs: true,
    props: {
      newTemplate: true,
    },
  },
  {
    path: `${viewpaths.useTemplateViewPath}`,
    component: SignRequestComponent,
    hideNavs: true,
    props: {
      useTemplate: true,
    },
  },
  {
    path: `${viewpaths.updateTemplateViewPath}`,
    component: SignRequestComponent,
    hideNavs: true,
    props: {
      updateTemplate: true,
    },
  },
  {
    path: `${viewpaths.updateTemplateViewPath}/:id`,
    component: SignRequestComponent,
    hideNavs: true,
    props: {
      updateTemplate: true,
    },
  },
  {
    path: `${viewpaths.sentSignRequestsViewPath}`,
    component: MySignRequests,
    hideNavs: false,
    props: {
      requestType: strings.sentRequest,
    },
  },
  {
    path: `${viewpaths.inboxViewPath}`,
    component: MySignRequests,
    hideNavs: false,
    props: {
      requestType: strings.inboxRequest,
    },
  },
  {
    path: "/requests/shared",
    component: MySignRequests,
    hideNavs: false,
    props: {
      requestType: strings.sharedRequest,
    },
  },
  {
    path: `${viewpaths.viewRequestViewPath}/:id`,
    component: ViewSignRequest,
    hideNavs: false,
  },
  {
    path: `${viewpaths.settingsRequestsViewPath}`,
    component: MySignRequests,
    hideNavs: false,
    props: {
      requestType: urls.configurations,
    },
  },
  {
    path: `${viewpaths.templatesViewPath}`,
    component: MyTemplates,
    hideNavs: false,
  },
  {
    path: `${viewpaths.webFormsViewPath}`,
    component: WebFormsList,
    hideNavs: false,
  },
  {
    path: `${viewpaths.webFormsViewPath}/builder/:id`,
    component: WebFormBuilder,
    hideNavs: true, // Typically builders want full screen, so hide standard navs
  },
  {
    path: `${viewpaths.webFormsAiViewPath}/:sessionId`,
    component: AiWebFormBuilder,
    hideNavs: true,
  },
  {
    path: `${viewpaths.contactsViewPath}`,
    component: Contacts,
    hideNavs: false,
  },
  {
    path: `${viewpaths.configurationsViewPath}`,
    component: Configurations,
    hideNavs: false,
  },
  {
    /*
      Phone-only Settings home. The reference has no desktop counterpart, so
      `SettingsIndex` redirects to the first reachable section at `sm` and up.
    */
    path: `${viewpaths.settingsViewPath}`,
    component: SettingsIndex,
  },
  {
    path: `${viewpaths.myProfileViewPath}`,
    component: MyProfile,
  },
  {
    path: `${viewpaths.securityViewPath}`,
    component: Security,
  },
  {
    path: `${viewpaths.securityPoliciesViewPath}`,
    component: SecurityPolicies,
  },
  {
    path: `${viewpaths.enterpriseSsoViewPath}`,
    component: EnterpriseSSOPage,
  },
  {
    path: `${viewpaths.rolesViewPath}`,
    component: RoleManagement,
  },
  {
    path: `${viewpaths.workspacesViewPath}`,
    component: Workspaces,
  },
  {
    path: `${viewpaths.workspaceEnrollmentViewPath}`,
    component: WorkspaceEnrollment,
  },
  {
    path: `${viewpaths.workspaceMembersViewPath}`,
    component: WorkspaceMembers,
  },
  {
    path: `${viewpaths.workspaceRolesViewPath}`,
    component: WorkspaceRoles,
  },
  {
    path: `${viewpaths.workspaceDetailsViewPath}`,
    component: WorkspaceDetails,
  },
  {
    path: `${viewpaths.workspaceBrandingViewPath}`,
    component: WorkspaceBranding,
  },
  {
    path: `${viewpaths.teamViewPath}`,
    component: Team,
    hideNavs: false,
  },
  {
    path: `${viewpaths.brandingViewPath}`,
    component: Branding,
    hideNavs: false,
  },
  {
    path: `${viewpaths.brandingViewPath}`,
    component: Branding,
    hideNavs: false,
  },
  {
    path: `${viewpaths.documentSettingsViewPath}`,
    component: DocumentSettings,
    hideNavs: false,
  },
  {
    path: `${viewpaths.apiKeysViewPath}`,
    component: ApiKeys,
    hideNavs: false,
  },
  {
    path: `${viewpaths.delegationViewPath}`,
    component: Delegation,
    hideNavs: false,
  },
  {
    path: `${viewpaths.doSign}`,
    component: ParticipantScreen,
    hideNavs: true,
  },
  { path: `${viewpaths.viewMyPlanViewPath}`, component: MyPlan },
  { path: `${viewpaths.ticketsViewPath}`, component: Tickets },
  { path: `${viewpaths.viewTicketsViewPath}/:id`, component: ViewTicket },
  {
    path: `${viewpaths.renewableDocumentsViewPath}`,
    component: RenewableDocuments,
    hideNavs: false,
  },
  {
    path: `${viewpaths.renewableDocumentsListViewPath}`,
    component: RenewableDocumentsList,
  },
  {
    path: `${viewpaths.insightsViewPath}`,
    component: InsightsPage,
  },
  {
    path: `${viewpaths.contractSearchViewPath}`,
    component: ContractSearchComponent,
  },
  {
    path: `${viewpaths.contractCompareViewPath}`,
    component: ContractCompareComponent,
  },
  {
    path: `${viewpaths.reports}`,
    component: (props: any) => <ReportsTabsPage {...props} />,
  },
  {
    path: `${viewpaths.reportsEnvelopes}`,
    component: (props: any) => <EnvelopesReport {...props} />,
    // hideNavs : false,
  },
];

const MainApp = () => {
  const account = useAppSelector(selectAccount);
  const isAuthenticated = useAppSelector(selectAuthenticated);
  const [signRequest, setSignRequest] =
    useState<SignRequest>(initialSignRequest);
  const [planLimits, setPlanLimits] = useState<Map<string, PlanLimit>>(
    new Map(),
  );
  const [selectedParticipant, setSelectedParticipant] = useState(
    initialSelectedParticipant,
  );
  const [isRequest, setIsRequest] = useState<boolean>(false);
  const [planStatus, setPlanStatus] = useState<string>("");
  const [selectedField, setSelectedField] =
    useState<FileField>(initialSelectedField);
  const [selectedFile, setSelectedFile] =
    useState<RequestFile>(initialSelectedFile);
  const [cards, setCards] = useState<Card[]>([]);
  const [currentPlan, setCurrentPlan] = useState<AppPlan>({} as AppPlan);
  const [accountDetails, setAccountDetails] = useState<AccountDetails>(
    {} as AccountDetails,
  );
  const dispatch = useAppDispatch();

  const hasBillingFetchPermission = hasAccessTo(
    strings.resources.billing,
    strings.fetchPermission,
  );

  useEffect(() => {
    if (hasBillingFetchPermission) {
      fetchPlanUsage(setPlanLimits);
      fetchAccountPlanDetails(
        account,
        setPlanStatus,
        setCurrentPlan,
        setCards,
        setAccountDetails,
      );
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(loadFeatures());
    }
  }, [isAuthenticated, dispatch]);

  const setAspectRatio = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isLandscape = width > height;

    let isMobile: boolean;

    if (isLandscape) {
      // Landscape mode
      // Phone landscape:  height ≤ 480  (e.g. 812x375 → height=375)
      // Tablet landscape: height 481-899 (e.g. 1024x768 → height=768)
      // Desktop:          height ≥ 900
      isMobile =
        width < 900 || (width < 1024 && height <= 899 && height > 480)
          ? height <= 480 // only phones in landscape
          : false;
    } else {
      // Portrait mode
      // Phone portrait:  width ≤ 480  (e.g. 375x812 → width=375)
      // Tablet portrait: width 481-899 (e.g. 768x1024 → width=768)
      // Desktop:         width ≥ 900
      isMobile = width <= 899;
    }
    dispatch(setScreen(isMobile));
  }, [dispatch]);

  useEffect(() => {
    setAspectRatio();
    const rafId = window.requestAnimationFrame(() => setAspectRatio());
    const timeoutId = window.setTimeout(() => setAspectRatio(), 50);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [isAuthenticated, setAspectRatio]);

  // Inside a partner iframe the window IS the iframe, and partners resize it (responsive layouts, the
  // SDK's auto-height), so re-pick the desktop/mobile screen on resize. Not applied to the app itself.
  useEffect(() => {
    if (!IS_EMBED) return;
    window.addEventListener("resize", setAspectRatio);
    return () => window.removeEventListener("resize", setAspectRatio);
  }, [setAspectRatio]);

  const protectedRoutes = useMemo(
    () =>
      routes.map((route, index) => (
        <AuthGuard
          key={index}
          exact
          isLoggedIn={isAuthenticated}
          path={route.path}
          component={route.component}
          componentProps={route.props}
        />
      )),
    [isAuthenticated],
  );

  // Stable identity so App re-renders (theme, features load, etc.) don't remount every routed
  // screen — remounting was refiring each screen's data fetch on refresh. Only auth transitions
  // recreate it.
  const routedShell = useCallback(
    (props: any) => {
      const currentRoute = routes.find((route) => {
        const paths = Array.isArray(route.path) ? route.path : [route.path];
        return paths.some((p) =>
          matchPath(props.location.pathname, { path: p, exact: true }),
        );
      });
      return (
        <Layout hideNavs={currentRoute?.hideNavs ?? false}>
          <main id="main-content" tabIndex={-1} style={{ outline: "none" }}>
            <Switch>{protectedRoutes}</Switch>
          </main>
          {isAuthenticated && <InActiveUserModel />}
        </Layout>
      );
    },
    [protectedRoutes, isAuthenticated],
  );

  return (
    <GeneralContext.Provider
      value={{
        signRequest,
        setSignRequest,
        selectedParticipant,
        setSelectedParticipant,
        selectedField,
        setSelectedField,
        selectedFile,
        setSelectedFile,
        isRequest,
        setIsRequest,
      }}
    >
      <AccountContext.Provider
        value={{
          planLimits,
          setPlanLimits,
          planStatus,
          setPlanStatus,
          cards,
          setCards,
          currentPlan,
          setCurrentPlan,
          accountDetails,
          setAccountDetails,
        }}
      >
        <Box>
          <Router history={history}>
            <ScrollToTop />
            <RouteAnnouncer />
            <TourProvider>
              <Switch>
                <Route
                  exact
                  path={[viewpaths.landingViewPath, viewpaths.loginViewPath]}
                  component={Login}
                />
                <Route
                  exact
                  path={[viewpaths.oAuthRegisterViewPath]}
                  component={OAuthRegister}
                />
                <Route
                  exact
                  path={[viewpaths.registerViewPath]}
                  component={Register}
                />
                <Route
                  exact
                  path={[
                    viewpaths.registrationSuccessViewPath,
                    viewpaths.verificationPendingViewPath,
                  ]}
                  component={RegistrationSuccess}
                />
                <Route
                  exact
                  path={[viewpaths.forgotPasswordViewPath]}
                  component={ForgotPassword}
                />
                <Route
                  exact
                  path={[viewpaths.resetPasswordViewPath]}
                  component={NewPassword}
                />
                <Route
                  exact
                  path={[viewpaths.checkYourEmailView]}
                  component={CheckYourMail}
                />
                <Route
                  exact
                  path={[viewpaths.onBoardCheckoutViewPath]}
                  component={VerifyPassword}
                />
                <Route
                  exact
                  path={[viewpaths.onBoardingClient]}
                  component={DirectRegister}
                />
                <Route
                  exact
                  path={[viewpaths.verifyTOTPViewPath]}
                  component={OTP}
                />
                <Route
                  exact
                  path={[viewpaths.setupMFAViewPath]}
                  component={SetupMFA}
                />
                <Route
                  exact
                  path={[viewpaths.expiryPasswordViewPath]}
                  component={ExpiryPassword}
                />
                <Route
                  exact
                  path={[viewpaths.ssoCallbackViewPath]}
                  component={SsoCallback}
                />
                <Route
                  exact
                  path={[viewpaths.mcpAuthorizeViewPath]}
                  component={McpAuthorize}
                />
                <Route
                  exact
                  path={[viewpaths.awsMarketplaceSetupViewPath]}
                  component={AwsMarketplaceSetup}
                />
                <Route
                  exact
                  path={[viewpaths.oauthConsentViewPath]}
                  component={OAuthConsent}
                />
                <Route
                  exact
                  path={[viewpaths.doSign]}
                  component={ParticipantScreen}
                />
                <Route
                  exact
                  path={[viewpaths.declineRequest]}
                  component={DeclineRequestPage}
                />
                <Route exact path={[EMBED_SIGN_PATH]} component={EmbedSignScreen} />
                <Route
                  exact
                  path={[EMBED_EDITOR_PATH]}
                  component={EmbedEditorScreen}
                />
                <Route
                  exact
                  path={[viewpaths.inPersonHost]}
                  component={InPersonHostLinkScreen}
                />
                <Route
                  exact
                  path={[viewpaths.linkExpired]}
                  component={LinkExpiredComponent}
                />
                <PrivateRoute
                  isLoggedIn={isAuthenticated}
                  path={routes.map((route) => route.path)}
                  component={routedShell}
                />
                <Route path={""} component={PageNotFound} />
              </Switch>
            </TourProvider>
          </Router>
          <Notifications />
        </Box>
      </AccountContext.Provider>
    </GeneralContext.Provider>
  );
};

const ClearCacheComponent = withClearCache(MainApp);

export default App;
